import { useTranslation } from "react-i18next";

import {
  OfferProgress,
  asRiskLevel,
  asYieldType,
  type RobotDetail,
  type ShowcaseReturns,
} from "@/entities/robot";
import { formatMoney, formatMomentDay, formatPercent, percentTone } from "@/shared/lib";
import { Icon, Panel } from "@/shared/ui";
import type { IconName } from "@/shared/ui";

/**
 * Блок ключевых метрик робота (доработки §4.1).
 *
 * Шесть позиций таблицы §4.1 одной сеткой: активные пользователи, совокупный
 * реализованный результат, капитал под управлением, доходность с явно
 * названным периодом и методом расчёта, риск с пояснением и период работы.
 * Для лимитированного робота ниже — заполненность пула, дедлайн и личный
 * остаток.
 *
 * Данные берутся из КАРТОЧКИ (`/robots/{id}`), а не из аналитики. Это важно
 * дважды: блок рисуется вместе с шапкой, не дожидаясь тяжёлого `/analytics`, и
 * продолжает работать, когда аналитика недоступна (§4.2) — ровно ради этого
 * `aum` продублирован в `social_stats`.
 *
 * Пустая плитка не рисуется вовсе. «Под управлением 0 USDT» — не факт о
 * роботе, а признание, что показывать нечего (ТЗ §33, §4.2: не показывать
 * фиктивные нули).
 *
 * Заменил собой `SocialStatsBlock`: две его плитки входят сюда, и показывать
 * те же числа дважды на одном экране значило бы заставить сверять, не
 * разошлись ли они.
 */
type Tile = { key: string; icon: IconName; text: string; note?: string; tone?: string };

export function KeyMetricsBlock({
  robot,
  returns,
  showAum = true,
}: {
  robot: RobotDetail;
  returns: ShowcaseReturns;
  /** Флаг оператора `visible_blocks.aum` (ТЗ §26). */
  showAum?: boolean;
}) {
  const { t } = useTranslation();
  const social = robot.social_stats;
  const yieldType = asYieldType(robot.yield_type);
  const risk = asRiskLevel(robot.risk_level);

  const tiles: Tile[] = [];

  if (social.active_users_count !== null) {
    tiles.push({
      key: "activeUsers",
      icon: "userCircle",
      text: String(social.active_users_count),
    });
  }

  if (social.total_realized_user_pnl !== null) {
    tiles.push({
      key: "realized",
      icon: "coin",
      text: formatMoney(social.total_realized_user_pnl, {
        sign: "always",
        suffix: social.pnl_currency,
      }),
      tone: percentTone(social.total_realized_user_pnl),
    });
  }

  if (showAum && social.aum !== null && social.aum !== undefined) {
    tiles.push({
      key: "aum",
      icon: "coinStack",
      text: formatMoney(social.aum, { suffix: social.pnl_currency }),
    });
  }

  // Период назван в подписи плитки, метод расчёта — отдельной строкой под
  // числом. §4.1 требует обоих: «за 30 дней» без «целевая» обещает факт.
  tiles.push({
    key: "yield",
    icon: "trendUp",
    text: formatPercent(returns.d30, { sign: "always" }),
    note: t(`robots.yieldType.${yieldType}`),
    tone: percentTone(returns.d30),
  });

  tiles.push({
    key: "risk",
    icon: "shieldCheck",
    text: t(`robots.risk.${risk}`),
    // Пояснение оператора, а при пустом — дефолтное по уровню: три буквы без
    // расшифровки не объясняют ничего, а объяснить их надо у каждого робота.
    note: robot.risk_note ?? t(`robots.riskNote.${risk}`),
  });

  tiles.push({
    key: "uptime",
    icon: "chartLineUp",
    text: t("robots.metrics.uptimeDays", { count: robot.uptime_days }),
    note: t("robots.metrics.uptimeSince", { date: formatMomentDay(robot.launched_at) }),
  });

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-body font-semibold text-text-primary">
        {t("robots.metrics.title")}
      </h2>

      <ul className="grid grid-cols-2 gap-2">
        {tiles.map((tile) => (
          <Panel as="li" key={tile.key} radius="lg" className="flex min-w-0 flex-col gap-1.5 p-3">
            <span className="flex items-center gap-1.5 text-micro font-medium text-text-tertiary">
              <Icon name={tile.icon} size={12} />
              <span className="truncate">{t(`robots.metrics.${tile.key}`)}</span>
            </span>
            <span className={`text-body font-semibold ${tile.tone ?? "text-text-primary"}`}>
              {tile.text}
            </span>
            {tile.note !== undefined ? (
              <span className="text-micro leading-4 font-normal text-text-tertiary">
                {tile.note}
              </span>
            ) : null}
          </Panel>
        ))}
      </ul>

      <p className="text-micro leading-4 font-normal text-text-tertiary">
        {t("robots.social.realizedHint")}
      </p>

      {/* Витринные цифры оператора обязаны быть отличимы от посчитанных —
          критерий P0 §12 «нет hardcoded production-цифр». Показывать их всё
          ещё можно (на свежем стенде агрегат сообщал бы не о роботах, а о
          том, что базу только что накатили), выдавать за факт — нет. */}
      {social.source === "declared" ? (
        <p className="text-micro leading-4 font-normal text-status-warning">
          {t("robots.social.declaredHint")}
        </p>
      ) : null}

      {robot.limited !== null ? (
        <Panel as="section" radius="lg" className="flex flex-col gap-2 p-4">
          <h3 className="text-caption font-semibold text-text-primary">
            {t("robots.metrics.limitedTitle")}
          </h3>
          <OfferProgress offer={robot.limited} />
          {/* Личный остаток считает бэкенд: он один знает и пул, и уже
              внесённое этим человеком (правило ТЗ №5). */}
          {robot.limited.accepts_entries && robot.limited_available ? (
            <p className="text-micro font-medium text-text-secondary">
              {t("robots.metrics.limitedAvailable", {
                amount: formatMoney(robot.limited_available, { suffix: "USDT" }),
              })}
            </p>
          ) : null}
        </Panel>
      ) : null}
    </section>
  );
}
